import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Activity, Layers, Leaf, Repeat, TrendingUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  ClientLifecycleEvent,
  ComparableFmv,
  ClientTelemetryDay,
  TelemetryScoreClientResponse,
  TelemetryScoreHistoryClientResponse,
  TelemetryScoreHistoryClientItem,
  PassportEnvironmental,
} from '@siliconx/shared';
import { conditionLabel } from '@siliconx/shared/ui/ConditionBadge';
import { ComparableFmvPanel } from '@siliconx/shared/ui/ComparableFmvPanel';
import { TIER_ACCENT, coverageTier } from '@siliconx/shared/ui/TelemetryScoreCallout';
import { PassportQrButton } from '@siliconx/shared/ui/PassportQrButton';
import { effectiveSystemFmvLabel } from '@siliconx/shared/ui/assetComponentsFooter';
import { MaskedSerial } from '../asset-detail/MaskedSerial';
import { FmvConfidenceStrip } from './FmvBasisDrawer';
import { DIRECTIONAL_BASELINE_LABEL } from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { qk } from '../../lib/queryKeys';
import { useAssetComponents } from '../../lib/assetComponents';
import type { ClientFmvSnapshot } from '../asset-detail/asset-detail-types';
import { Card, TileLabel, baseline30d, downloadCsv, MiniTable, TableToggleFooter, money, stripMfrPrefix, assetIsListable, collapseSameDayDuplicates } from './shared';
import { OverviewTab } from './tabs/OverviewTab';
import { ValueTab } from './tabs/ValueTab';
import { HealthTab } from './tabs/HealthTab';
import { SustainabilityTab } from './tabs/SustainabilityTab';
import { LifecycleTab } from './tabs/LifecycleTab';

// Re-exported so route/test call sites keep importing from this module
// (`../../routes/assets/$assetId.tsx`, `__tests__/asset-detail-tabs.test.ts`)
// without a second import path to track.
export { stripMfrPrefix, assetIsListable };

/** Confidence qualifier word per coverage tier — pairs with TIER_ACCENT so
 * the Telemetry health tile can read "<score> · <tier> confidence (N%
 * coverage)" instead of coloring the score number itself (SIL-333). */
const TIER_LABEL: Record<'low' | 'moderate' | 'high', string> = {
  low: 'low',
  moderate: 'moderate',
  high: 'high',
};

/**
 * Asset detail v2 — the "at-a-glance" redesign from the Asset Detail
 * Visualizations design doc (mock 6a + passport preview card 7d). Rendered
 * as the default on /assets/$assetId; `?v=1` keeps the legacy layout
 * reachable until it (and this search-param gate) is deleted.
 *
 * SIL-565: the five-tab content (Overview / Value / Health & condition /
 * Sustainability / Lifecycle) has been split into per-tab modules under
 * `./tabs/` — this file is now the tab shell (tab bar, top-of-page tiles,
 * page assembly) plus the passport-preview card the route also imports.
 *
 * Data is entirely the client projection the v1 page already fetches — this
 * component receives it as props (plus one score-history query shared with
 * v1's query key). Locked display logic (design grilling, Jul 2026):
 *  - all deltas use a 30-day window; delta fragments are dropped when no
 *    baseline exists in the window
 *  - health-trend bands are the REAL horizon bands (85/70/50 —
 *    healthy/degraded/warning/critical), not the mock's invented 85/60 cuts
 *  - coverage gaps (overallCoverage < 1) are flagged on the trend, so a dip
 *    reads as "telemetry gap", not condition
 *  - subsystem bars zoom to [min rounded down to 10, 100]
 *  - telemetry delta chips: temp/power warn on ≥ +1°C / ≥ +5 W rises, never
 *    "good"; utilization is workload, always neutral
 */

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

/** The five content groups, in reading order. `id` is the ?tab= value.
 * Ids stay their original values (`info`/`value`/...) for URL back-compat —
 * only the reader-facing labels changed to match the prototype's naming
 * (wayfinder-iter2-spec.md §7-B3): "Asset information" → "Overview",
 * "Market value" → "Value". */
const ASSET_DETAIL_TABS = [
  {
    id: 'info',
    icon: Layers,
    label: 'Overview',
    description: 'Model identity, specs, purchase and location records, and supporting documents.',
  },
  {
    id: 'value',
    icon: TrendingUp,
    label: 'Value',
    description: 'FMV forecast for this asset if held past the refresh cliff.',
  },
  {
    id: 'health',
    icon: Activity,
    label: 'Health & condition',
    description:
      'The evidence behind the condition grade — health scoring and raw device telemetry.',
  },
  {
    id: 'sustainability',
    icon: Leaf,
    label: 'Sustainability',
    description:
      'Carbon reported by GHG Protocol scope — Scope 3 embodied at manufacture, Scope 2 operational while in service. Reported separately, never summed.',
  },
  {
    id: 'lifecycle',
    icon: Repeat,
    label: 'Lifecycle',
    description: 'Every event is recorded to the Silicon Passport — the chain is the evidence.',
  },
] as const;

export type AssetDetailTabId = (typeof ASSET_DETAIL_TABS)[number]['id'];

export function isAssetDetailTabId(v: unknown): v is AssetDetailTabId {
  return ASSET_DETAIL_TABS.some((t) => t.id === v);
}

/**
 * Underline tab bar for the five groups. Sticky beneath the app header so the
 * reader can change group from anywhere in a long panel without scrolling back
 * up. Roving-tabindex keyboard model (arrows / Home / End), per the WAI-ARIA
 * tabs pattern — only the selected tab is in the tab order.
 */
function TabBar({
  active,
  onSelect,
}: {
  active: AssetDetailTabId;
  onSelect: (id: AssetDetailTabId) => void;
}) {
  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const i = ASSET_DETAIL_TABS.findIndex((t) => t.id === active);
    const next =
      e.key === 'ArrowRight'
        ? (i + 1) % ASSET_DETAIL_TABS.length
        : e.key === 'ArrowLeft'
          ? (i - 1 + ASSET_DETAIL_TABS.length) % ASSET_DETAIL_TABS.length
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? ASSET_DETAIL_TABS.length - 1
              : -1;
    const target = ASSET_DETAIL_TABS[next];
    if (!target) return;
    e.preventDefault();
    const id = target.id;
    onSelect(id);
    document.getElementById(`asset-tab-${id}`)?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label="Asset detail sections"
      onKeyDown={onKeyDown}
      // bg-zinc-50 matches the scroll container's ground so cards pass behind
      // the bar rather than through it. The negative margins bleed the bar into
      // main's p-4 / sm:p-6 and the matching padding puts the labels back where
      // they were — without that, content scrolls visibly through the padding
      // band above a `top-0` sticky element.
      // The negative `top` cancels the scroll container's own padding: a sticky
      // element's constraint rect is inset by it, so `top-0` would pin the bar
      // 24px low and let cards scroll through the band above it.
      className="sticky -top-4 z-10 -mx-4 -mt-4 flex flex-wrap gap-x-1 border-b border-zinc-200 bg-zinc-50 px-4 pt-4 sm:-top-6 sm:-mx-6 sm:-mt-6 sm:px-6 sm:pt-6"
    >
      {ASSET_DETAIL_TABS.map(({ id, icon: Icon, label }) => {
        const on = id === active;
        return (
          <button
            key={id}
            id={`asset-tab-${id}`}
            role="tab"
            type="button"
            aria-selected={on}
            aria-controls={`asset-panel-${id}`}
            tabIndex={on ? 0 : -1}
            onClick={() => onSelect(id)}
            className={`-mb-px flex items-center gap-2 rounded-sm border-b-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
              on
                ? 'border-brand-600 text-zinc-900'
                : 'border-transparent text-zinc-600 hover:text-zinc-800'
            }`}
          >
            <Icon className={`h-3.5 w-3.5 ${on ? 'text-brand-600' : 'text-zinc-400'}`} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Passport preview card (7d)
// ---------------------------------------------------------------------------

export function PassportPreviewCard({
  fingerprint,
  serialLabel,
  serial,
}: {
  fingerprint: string;
  /** SIL-1269: a NON-serial label (asset tag / id prefix) - it feeds the QR label. */
  serialLabel: string;
  /** When given, the card shows the masked serial (Reveal) instead of the label text. */
  serial?: { assetId: string; hasSerial: boolean };
}) {
  // Absolute: the QR is read by a phone that has no notion of this origin.
  const passportUrl = `${window.location.origin}/passport/${fingerprint}`;
  function share() {
    void navigator.clipboard.writeText(passportUrl).then(() => toast.success('Passport link copied'));
  }
  return (
    <div className="flex flex-none items-center gap-3 rounded-lg border border-zinc-200 bg-white px-3.5 py-2.5 text-zinc-900">
      {/* Was a decorative glyph from the design mock that encoded nothing;
          now the real code, tappable for a scannable rendering. */}
      <PassportQrButton
        passportUrl={passportUrl}
        serialLabel={serialLabel}
        onCopied={() => toast.success('Passport link copied')}
      />
      <div>
        <div className="text-xs font-semibold">Silicon Passport</div>
        <div className="mt-px font-mono text-[13px] text-zinc-500">
          {serial ? (
            <MaskedSerial assetId={serial.assetId} hasSerial={serial.hasSerial} emptyLabel={serialLabel} />
          ) : (
            serialLabel
          )}
        </div>
        <div className="mt-0.5 text-xs font-semibold text-brand-700">
          <Link to="/passport/$fingerprint" params={{ fingerprint }} className="hover:underline">
            Open
          </Link>
          {' · '}
          <button type="button" onClick={share} className="hover:underline">
            Share &rarr;
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scorecard tiles — stay above the tab bar: FMV, health, and condition are
// the answer to the page and belong to every group, so they are never a tab
// away.
// ---------------------------------------------------------------------------

interface ApiError extends Error {
  status?: number;
}

/** Staleness tier for the "priced <date>" caption (SIL-334) — hardware in
 * this category depreciates roughly 50%/year, so a same-styled "priced Dec
 * 31, 2024" reading as current is actively misleading. Neutral <=30d,
 * amber >30d, with a re-pricing action surfaced only past 90d. */
function fmvStalenessDays(pricedAt: string): number {
  return Math.floor((Date.now() - new Date(pricedAt).getTime()) / (24 * 3600 * 1000));
}

export function FmvTile({
  history: rawHistory,
  assetId,
  serialLabel,
}: {
  history: ClientFmvSnapshot[];
  assetId: string;
  serialLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const history = collapseSameDayDuplicates(rawHistory);
  const latest = history[0] ?? null;
  const base = baseline30d(history, (r) => r.pricedAt);
  const deltaPct =
    latest && base && base.fmvMedUsd !== 0
      ? ((latest.fmvMedUsd - base.fmvMedUsd) / base.fmvMedUsd) * 100
      : null;
  const stalenessDays = latest ? fmvStalenessDays(latest.pricedAt) : null;
  const stale = stalenessDays != null && stalenessDays > 30;
  const veryStale = stalenessDays != null && stalenessDays > 90;

  // SIL-1290: the chassis has no priced snapshot yet — fall back to the sum
  // of priced children (`effectiveSystemFmv`, source: 'components_only') so
  // the card doesn't read "not yet priced" while $17k of accelerators sit
  // priced right below it. Reuses AssetComponentsCard's query key/cache, so
  // this costs no extra request when that card is already mounted, and only
  // fires at all when there's no snapshot history to show instead.
  const { data: componentsData } = useAssetComponents(assetId, latest === null);
  const effective = latest === null ? (componentsData?.effectiveSystemFmv ?? null) : null;
  const effectiveLabel = effectiveSystemFmvLabel(effective);

  const visible = showAll ? history : history.slice(0, 6);
  return (
    <Card className="p-5">
      <TileLabel muted={!latest}>Fair Market Value</TileLabel>
      <div className="mt-2 font-mono text-xl font-semibold text-zinc-900">
        {latest ? money(latest.fmvMedUsd) : effective ? money(effective.valueUsd) : '—'}
      </div>
      {/* SIL-1828 (R2): label only (server flag); no component detail for customers. */}
      {latest?.isDirectionalBaseline && (
        <div
          data-testid="fmv-directional-label"
          className="mt-1 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700"
        >
          {DIRECTIONAL_BASELINE_LABEL}
        </div>
      )}
      <div className={`mt-1 font-mono text-[13px] ${stale ? 'text-amber-700' : 'text-zinc-500'}`}>
        {deltaPct != null &&
          // Matches HealthTile's stable-state treatment: a sub-0.05% move
          // rounds to "0.0%" and shouldn't read as a directional ↑/↓.
          (Math.abs(deltaPct) < 0.05 ? (
            <span>→ stable /30d ·{' '}</span>
          ) : (
            <span className={deltaPct < 0 ? 'text-amber-700' : 'text-emerald-700'}>
              {deltaPct < 0 ? '↓' : '↑'} {deltaPct >= 0 ? '+' : ''}
              {deltaPct.toFixed(1)}% /30d ·{' '}
            </span>
          ))}
        {latest ? `priced ${formatDate(latest.pricedAt)}` : (effectiveLabel ?? 'not yet priced')}
        {stale && ` (${stalenessDays}d ago)`}
      </div>
      {/* SIL-920: confidence/marketability/comp-recency strip — picked over
          the bare info-glyph trigger after a 5-variant prototype review. */}
      <FmvConfidenceStrip snapshot={latest} />
      {veryStale && (
        <a
          href={`mailto:support@siliconx.com?subject=${encodeURIComponent(
            `Request re-pricing — asset ${serialLabel}`,
          )}&body=${encodeURIComponent(
            `Asset ${serialLabel} (${assetId}) was last priced ${stalenessDays} days ago (${
              latest ? formatDate(latest.pricedAt) : ''
            }). Please recompute its FMV.`,
          )}`}
          className="mt-1.5 inline-block text-xs font-medium text-amber-700 underline hover:text-amber-800"
        >
          Request re-pricing
        </a>
      )}
      {history.length > 0 && (
        <>
          <TableToggleFooter
            open={open}
            onToggle={() => setOpen(!open)}
            onExport={() =>
              downloadCsv(
                'fmv-history.csv',
                ['Date', 'FMV median'],
                history.map((r) => [formatDate(r.pricedAt), r.fmvMedUsd]),
              )
            }
          />
          {open && (
            <>
              <MiniTable
                header={['Date', 'FMV median']}
                rows={visible.map((r) => [formatDate(r.pricedAt), money(r.fmvMedUsd)])}
                numericCols={[1]}
              />
              {!showAll && history.length > 6 && (
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="mt-1.5 text-xs text-zinc-500 hover:text-zinc-700"
                >
                  Showing 6 of {history.length} · view all
                </button>
              )}
            </>
          )}
        </>
      )}
    </Card>
  );
}

function HealthTile({
  score,
  history,
}: {
  score: TelemetryScoreClientResponse | null;
  history: TelemetryScoreHistoryClientItem[];
}) {
  const base = baseline30d(history, (r) => r.scoredAt);
  const delta = score && base ? score.overall - base.overallScore : null;
  const trend =
    delta == null ? null : Math.abs(delta) < 1 ? '→ stable' : delta > 0 ? `↑ +${delta.toFixed(0)}` : `↓ ${delta.toFixed(0)}`;
  // SIL-333: coverage (confidence) is a caveat on the score, not the score's
  // own color — a red "94" read as the hardware itself being bad rather than
  // the *reading* being thin. The number stays neutral; the tier accent now
  // lives on the confidence qualifier below it, framed explicitly as
  // "<tier> confidence (N% coverage)" so low coverage reads as "we don't
  // know" rather than "it's failing."
  const tier = score ? coverageTier(score.coverage) : null;
  const accent = tier ? TIER_ACCENT[tier] : 'text-zinc-600';
  const tierLabel = tier ? TIER_LABEL[tier] : null;
  return (
    <Card className="p-5">
      <TileLabel muted={!score}>Telemetry health</TileLabel>
      <div className="mt-2 font-mono text-xl font-semibold text-zinc-900">
        {score ? Math.round(score.overall) : '—'}
      </div>
      <div className="mt-1 font-mono text-[13px]">
        {score == null ? (
          <span className="text-zinc-500">not yet scored</span>
        ) : (
          <>
            {trend && <span className="text-zinc-500">{trend} · </span>}
            <span className={accent}>
              {tierLabel} confidence ({Math.round(score.coverage * 100)}% coverage)
            </span>
          </>
        )}
      </div>
    </Card>
  );
}

/** Band-appropriate text color — mirrors ConditionBadge's palette so High
 * wear reads as stress (amber), never green. */
const CONDITION_TEXT_CLASS: Record<string, string> = {
  like_new: 'text-emerald-700',
  normal_enterprise_wear: 'text-zinc-700',
  high_wear: 'text-amber-700',
};

function ConditionTile({
  condition,
  gradedAt,
}: {
  condition: string | null;
  gradedAt: string | null;
}) {
  return (
    <Card className="p-5">
      <TileLabel muted={!condition}>Condition</TileLabel>
      <div
        className={`mt-2 text-base font-semibold ${
          (condition && CONDITION_TEXT_CLASS[condition]) || 'text-zinc-400'
        }`}
      >
        {condition ? conditionLabel(condition) : '—'}
      </div>
      <div className="mt-1 font-mono text-[13px] text-zinc-500">
        {gradedAt ? `graded from telemetry · ${formatDate(gradedAt)}` : 'not yet graded'}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page assembly
// ---------------------------------------------------------------------------

export interface AssetDetailV2Props {
  asset: {
    id: string;
    hasSerial: boolean;
    assetTag?: string | null;
    assetClassId: string;
    condition: string | null;
    manufacturerId: string | null;
    manufacturerName: string | null;
    modelName: string | null;
    generation: string | null;
    subClass: string | null;
    classAttributes: Record<string, unknown> | null;
    specFacts: Record<string, string | number | boolean>;
    specDisplaySchema: unknown;
    latestFmv: { medUsd: number; pricedAt: string; conditionBand: string } | null;
    lifecycleAllowsListing: boolean;
    lifecycleStateId: string | null;
    lifecycleStateName: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  };
  snapshotHistory: ClientFmvSnapshot[];
  telemetryRecent: ClientTelemetryDay[];
  /** Lifecycle-stage transitions (active/idle/decommissioned) for the timeline. */
  lifecycleEvents: ClientLifecycleEvent[];
  score: TelemetryScoreClientResponse | null;
  /** Passport environmental block (SIL-190) — null while loading/404. */
  environmental: PassportEnvironmental | null;
  /** SIL-1155: informational comparable-FMV range (absent unless the API sent it). */
  comparableFmv?: ComparableFmv;
  /** Named slots for cards owned/fetched by the route (unchanged props),
   * placed into the section grouping below. */
  provenance?: React.ReactNode;
  sellerDetails?: React.ReactNode;
  locationTeam?: React.ReactNode;
  attachments?: React.ReactNode;
  lifecycleManagement?: React.ReactNode;
  listForSale?: React.ReactNode;
  /** Probe asset grouping (Plan 4): the Components card, directly under the
   * tiles so "System FMV above, components below" holds on every tab. */
  components?: React.ReactNode;
}

export function AssetDetailV2({
  asset,
  snapshotHistory,
  telemetryRecent,
  lifecycleEvents,
  score,
  environmental,
  comparableFmv,
  provenance,
  sellerDetails,
  locationTeam,
  attachments,
  lifecycleManagement,
  listForSale,
  components,
}: AssetDetailV2Props) {
  const { data: historyResp } = useQuery<TelemetryScoreHistoryClientResponse, ApiError>({
    queryKey: qk.assets.telemetryScoreHistory(asset.id),
    queryFn: async () => {
      const res = await api.api.client.assets[':assetId']['telemetry-score'].history.$get({
        param: { assetId: asset.id },
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as TelemetryScoreHistoryClientResponse;
    },
    enabled: score != null,
  });
  const scoreHistory = historyResp?.data ?? [];
  // SIL-1269: non-serial label - it feeds the re-pricing mailto, which must never carry the serial.
  const serialLabel = asset.assetTag ?? asset.id.slice(0, 8);

  // Tab lives in ?tab= (see the route's validateSearch). `replace` so paging
  // through the five groups doesn't bury the previous page in the back stack.
  const search = useSearch({ from: '/assets/$assetId' });
  const navigate = useNavigate({ from: '/assets/$assetId' });
  const activeTab: AssetDetailTabId = search.tab ?? 'info';
  const activeTabMeta = ASSET_DETAIL_TABS.find((t) => t.id === activeTab) ?? ASSET_DETAIL_TABS[0];
  const onSelectTab = (tab: AssetDetailTabId) =>
    navigate({ search: (prev) => ({ ...prev, tab }), replace: true });

  return (
    // The three tiles stay above the tab bar: FMV, health, and condition are the
    // answer to the page and belong to every group, so they are never a tab away.
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <FmvTile history={snapshotHistory} assetId={asset.id} serialLabel={serialLabel} />
        <HealthTile score={score} history={scoreHistory} />
        <ConditionTile condition={asset.condition} gradedAt={score?.scoredAt ?? null} />
      </div>

      {/* SIL-1155: informational, visually distinct from the FMV tile; renders nothing when absent. */}
      <ComparableFmvPanel comparable={comparableFmv} />

      {components}

      <TabBar active={activeTab} onSelect={onSelectTab} />

      <div
        id={`asset-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`asset-tab-${activeTab}`}
        className="flex flex-col gap-3"
      >
        <p className="text-xs text-zinc-500">{activeTabMeta.description}</p>

        {activeTab === 'info' && (
          <OverviewTab
            asset={asset}
            gradedAt={score?.scoredAt ?? null}
            telemetryRecent={telemetryRecent}
            sellerDetails={sellerDetails}
            locationTeam={locationTeam}
            attachments={attachments}
          />
        )}

        {activeTab === 'value' && (
          <ValueTab assetId={asset.id} assetClassId={asset.assetClassId} history={snapshotHistory} />
        )}

        {activeTab === 'health' && (
          <HealthTab
            assetId={asset.id}
            score={score}
            scoreHistory={scoreHistory}
            telemetryRecent={telemetryRecent}
          />
        )}

        {activeTab === 'sustainability' && (
          <SustainabilityTab assetId={asset.id} environmental={environmental} />
        )}

        {activeTab === 'lifecycle' && (
          <LifecycleTab
            asset={asset}
            snapshotHistory={snapshotHistory}
            lifecycleEvents={lifecycleEvents}
            score={score}
            provenance={provenance}
            lifecycleManagement={lifecycleManagement}
            listForSale={listForSale}
          />
        )}
      </div>
    </div>
  );
}
